<template>
  <ActionHeader
    v-if="isTabletOrSmaller"
    page-header>
    <h5 class="my-xxs">
      {{ actionHeaderText }}
    </h5>
  </ActionHeader>
  <div class="account-membership-container pt-lg-xxl">
    <div class="row">
      <div class="offset-1 col-10 mb-xxl">
        <BreadCrumb
          v-if="!isTabletOrSmaller"
          :current-location="currentLocation"
          :crumbs="breadcrumbs.slice(0, breadcrumbs.length - 1)"/>
      </div>
      <router-view
        #default="{ Component }"
        class="offset-1 col-10">
        <transition
          name="fade"
          mode="out-in">
          <component :is="Component"/>
        </transition>
      </router-view>
    </div>
    <div style="position: fixed; bottom: 0; left: 0; right: 0; z-index: 1000;"/>
  </div>
</template>

<script setup>
import BreadCrumb from '@/components/global/sequin/BreadCrumb.vue'
import { useAccountStore } from '@/stores/account.js'
import { storeToRefs } from 'pinia'
import { onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import useScreenSize from '@shared/composables/screenSize.js'
import ActionHeader from '@/components/global/sequin/ActionHeader.vue'

const { isTabletOrSmaller } = useScreenSize()
const accountStore = useAccountStore()
const { breadcrumbs, currentLocation, actionHeaderText } = storeToRefs(accountStore)
const { setBreadcrumbs, setActionHeaderText } = accountStore
const route = useRoute()

const updateBreadcrumbsAndHeader = () => {
  const routeName = route.name

  const baseBreadcrumbs = [
    { name: 'Account', to: { name: 'account' } },
    { name: 'Membership', to: { name: 'membership-overview' } }
  ]

  const routeConfig = {
    'membership-overview': {
      breadcrumbs: baseBreadcrumbs,
      headerText: 'Membership'
    },
    'change-plan': {
      breadcrumbs: [
        ...baseBreadcrumbs,
        { name: 'Change Plan', to: { name: 'change-plan' } }
      ],
      headerText: 'Change Plan'
    },
    'cancel-confirmation': {
      breadcrumbs: [
        ...baseBreadcrumbs,
      ],
      headerText: 'Membership'
    },
    'pause-or-cancel': {
      breadcrumbs: [
        ...baseBreadcrumbs,
        { name: 'Pause or Cancel Membership', to: { name: 'pause-or-cancel' } }
      ],
      headerText: 'Pause Membership'
    },
    'pause': {
      breadcrumbs: [
        ...baseBreadcrumbs,
        { name: 'Pause or Cancel Membership', to: { name: 'pause-or-cancel' } },
        { name: 'Pause Membership', to: { name: 'pause' } }
      ],
      headerText: 'Pause Membership'
    },
    'cancel': {
      breadcrumbs: [
        ...baseBreadcrumbs,
        { name: 'Pause or Cancel Membership', to: { name: 'pause-or-cancel' } },
        { name: 'Cancel Membership', to: { name: 'cancel' } }
      ],
      headerText: 'Cancel Membership'
    },
    'cancel-benefits': {
      breadcrumbs: [
        ...baseBreadcrumbs,
        { name: 'Pause or Cancel Membership', to: { name: 'pause-or-cancel' } },
        { name: 'Cancel Membership', to: { name: 'cancel-benefits' } }
      ],
      headerText: 'Cancel Benefits'
    },
    'resume': {
      breadcrumbs: [
        ...baseBreadcrumbs,
        { name: 'Resume Membership', to: { name: 'resume' } }
      ],
      headerText: 'Resume Membership'
    },
    'rejoin': {
      breadcrumbs: [
        ...baseBreadcrumbs,
        { name: 'Rejoin', to: { name: 'rejoin' } }
      ],
      headerText: 'Rejoin'
    },
    'confirm-plan-change': {
      getBreadcrumbs: () => {
        const source = route.query.source
        if (source === 'resume') {
          return [
            ...baseBreadcrumbs,
            { name: 'Resume Membership', to: { name: 'resume' } },
            { name: 'Checkout' }
          ]
        } else if (source === 'rejoin') {
          return [
            ...baseBreadcrumbs,
            { name: 'Rejoin', to: { name: 'rejoin' } },
            { name: 'Checkout' }
          ]
        } else {
          // Default to change-plan
          return [
            ...baseBreadcrumbs,
            { name: 'Change Plan', to: { name: 'change-plan' } },
            { name: 'Checkout' }
          ]
        }
      },
      headerText: 'Checkout'
    }
  }

  const config = routeConfig[routeName] || {
    breadcrumbs: baseBreadcrumbs,
    headerText: 'Membership'
  }
  
  const breadcrumbsToSet = config.getBreadcrumbs ? config.getBreadcrumbs() : config.breadcrumbs
  setBreadcrumbs(breadcrumbsToSet)
  setActionHeaderText(config.headerText)
}

onMounted(() => {
  updateBreadcrumbsAndHeader()
})

watch(() => route.name, () => {
  updateBreadcrumbsAndHeader()
})

watch(() => route.query.source, () => {
  if (route.name === 'confirm-plan-change') {
    updateBreadcrumbsAndHeader()
  }
})
</script>

<style lang="scss" scoped>
.account-membership-container {
  @include adorn-gradient;
  min-height: 100dvh;
  width: calc(100vw - $desktop-nav-width);

  @include media-tablet-or-smaller {
    min-height: calc(100dvh - $nav-bar-height-mobile);
    width: 100vw;
  }

  overflow-x: hidden;
  overflow-y: auto;
}
</style>